'use client';
import { isValidElement, type ReactNode } from 'react';
import type { RailRowProps } from 'elowen-plugin-ui-kit';
import { cn } from '../../lib/utils';
import { usageMeterValue, usageProgressClass } from '../../lib/usageMeter';
import { ActionMenu } from './ActionMenu';
import { Progress } from './shadcn/progress';

/** The telemetry rail's shared layout: ONE grid every section is drawn on, core and plugin alike.
 *
 *  Every row has the same three columns: a 1rem lead (a status glyph or a section icon), the flexible
 *  text column and a right-aligned value column. Section heads, list rows, key/value rows and meters all
 *  use them, so a label starts at the same x in every section and every number ends on the same right
 *  edge. A meter splits the text column into a fixed label column and its bar, which is what makes the
 *  bars of different sections start together too.
 *
 *  Plugin bundles receive RailSection, RailList, RailRow and RailDot through
 *  `window.ElowenUiRuntime.components`; the heading and meter stay internal to the host. Shared public
 *  rows keep bundled sections aligned with the host's grid. */

/** Lead | text | value. Shared by the head and every row kind so the columns cannot drift apart. */
const GRID = 'grid grid-cols-[--spacing(4)_minmax(0,1fr)_auto] items-center gap-x-2';
/** The rail's one line height, for the head, every row kind and the meter alike: 28px on every pointer.
 *  A control row is NOT padded out to the 44px touch target: rows that tall, and even 32px rows under
 *  conversation-size text, stood apart as separate lines instead of reading as one list (owner, 29 Sep
 *  2026). 28px clears the WCAG 2.5.8 minimum of 24px, and the rows sit flush, so a tap never lands
 *  between two. */
const LINE = 'min-h-7';
/** A row's box: the line height, conversation-size text (`--text-conversation`, the size of the agent's
 *  answers beside the rail) and a hover wash that bleeds into the gutter while the text stays on the grid. */
const ROW_TEXT = 'text-conversation';
const ROW = cn('relative -mx-2 rounded-md px-2', ROW_TEXT, LINE);
const ROW_HIT = 'transition-colors hover:bg-accent/60';

/** Beside a detail, the name keeps its own width up to 70% of the text column and only then truncates,
 *  so a short name ("docs-audit") reads whole and the detail still gets a share of a long one. */
const NAME_BESIDE_DETAIL = 'max-w-[70%] shrink-0';

type Tone = 'success' | 'primary' | 'warning' | 'danger' | 'muted';
const DOT_TONE: Record<Tone, string> = {
  success: 'bg-success',
  primary: 'bg-primary',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  muted: 'bg-subtle-foreground',
};

/** The rail's status mark: a small dot in the tone of the state it reports. `live` adds the shared
 *  breathing ring (`.live-dot`), which the quiet effect modes stop like every other ambient motion. */
export function RailDot({ tone = 'success', live = false }: { tone?: Tone; live?: boolean }) {
  return <span aria-hidden className={cn('inline-block size-2 shrink-0 rounded-full', DOT_TONE[tone], live && 'live-dot')} />;
}

/** The count badge of a section head: one pill for every section's tally. */
function RailCount({ children }: { children: ReactNode }) {
  return (
    <span className="telemetry-section-count inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-small normal-case tabular-nums text-muted-foreground">
      {children}
    </span>
  );
}

/** The ONE heading every telemetry-rail section shares, on the rail grid: the section glyph in the lead
 *  column, the label in the text column, the tally on the value column's right edge. `count` is drawn
 *  as the shared pill; without a count the value column remains an empty grid cell.
 *
 *  The `telemetry-section-head` class is the seam a skin styles: Studio removes the uppercasing and the
 *  letter spacing, because "Other processes" is a phrase rather than a machine identifier. */
export function RailSectionHead({ label, icon, count }: { label: string; icon?: ReactNode; count?: ReactNode }) {
  return (
    <div className={cn('telemetry-section-head text-eyebrow uppercase text-subtle-foreground', LINE, GRID)}>
      <span className="flex items-center justify-center [&_svg]:size-3.5 [&_svg]:shrink-0">{icon ?? null}</span>
      {/* The label truncates: it is a translated string, and at the narrow end of the rail a long
          heading is otherwise a width floor the row cannot go under. */}
      <span className="min-w-0 truncate first-letter:uppercase">{label}</span>
      {count !== undefined ? <RailCount>{count}</RailCount> : <span />}
    </div>
  );
}

/** One rail section: the shared head over its rows. The rows go in a `RailList`. */
export function RailSection({ label, icon, count, testId, children }: {
  label: string;
  icon?: ReactNode;
  count?: ReactNode;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section data-testid={testId} className="flex min-w-0 flex-col">
      <RailSectionHead label={label} icon={icon} count={count} />
      {children}
    </section>
  );
}

/** The list the rows of a section sit in. `flush` is the list of `flush` meters outside the rail: one
 *  label | bar | figure grid whose side columns size to their widest entry, which each meter joins as a
 *  subgrid so the bars still start and end together. It is also the container that lets a meter stack
 *  when the list is narrower than 16rem: the label and figure then share the first line and the bar takes
 *  the second at the full width, because side by side a name and a money figure ("Balance", "$17.15")
 *  would leave a phone-width Settings row a bar of a few pixels. */
export function RailList({ children, flush = false }: { children: ReactNode; flush?: boolean }) {
  return <ul className={flush ? '@container grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3' : 'flex min-w-0 flex-col'}>{children}</ul>;
}

/** The row's contract is the KIT's (`RailRowProps` in `elowen-plugin-ui-kit`): one declaration of a
 *  component that the host renders and a bundle reaches through the runtime, re-exported here for the
 *  host's own call sites. Re-declaring it is how the two drifted apart before. */
export type { RailRowProps };

/** The 4-column variant a row takes when it carries a `labelLead`: the same lead and value columns, with
 *  the caller's prefix (the dot and whatever sits beside it) in a column of its own, so the NAME — and the
 *  note under it — starts after the prefix at whatever width that prefix really takes. */
const GRID_WITH_LEAD = cn(GRID, 'grid-cols-[--spacing(4)_auto_minmax(0,1fr)_auto]');

function RowCells({ lead, label, title, detail, value, mono, muted, labelAs, note, labelLead }: RailRowProps & { labelAs?: ReactNode }) {
  // A status dot is not a glyph for the gutter: it joins the text column, so the row's first visible mark
  // starts at the same x as the labels of the meter rows above it. Icons keep the lead column.
  const dotLead = isValidElement(lead) && lead.type === RailDot;
  // A caller that hands over the part of its label that is NOT the name gets a column for it, and the name
  // — with the note under it — starts after it. Without one the row keeps its three columns exactly.
  const prefixed = labelLead !== undefined;
  return (
    <>
      <span className="flex items-center justify-center [&_svg]:shrink-0">{dotLead ? null : lead ?? null}</span>
      {prefixed ? (
        <span className="flex min-w-0 items-center gap-2">
          {dotLead ? <span className="self-center">{lead}</span> : null}
          {labelLead}
        </span>
      ) : null}
      {/* A row with a note takes two lines: the name keeps the rail's own line height on the first one,
          with the dot and the figure column, and the note runs on a line of its own below. */}
      <span className={cn('flex min-w-0 items-baseline gap-2', note && cn(LINE, 'pt-1'))}>
        {dotLead && !prefixed ? <span className="self-center">{lead}</span> : null}
        {labelAs ?? (
          <span title={title} className={cn('min-w-0 truncate', detail ? NAME_BESIDE_DETAIL : 'shrink', mono && 'font-mono text-meta', muted ? 'text-muted-foreground' : 'text-foreground')}>{label}</span>
        )}
        {/* The detail takes what the name leaves, and at least the last 30% of the column. */}
        {detail ? <span className="min-w-0 flex-1 truncate text-meta text-muted-foreground">{detail}</span> : null}
      </span>
      <span className="flex items-center justify-end gap-1 text-meta tabular-nums text-muted-foreground">{value ?? null}</span>
      {/* The note's own line, under the name: it spans the text and the figure column, so a long note
          runs the full width of the row while the figures stay on the first line. A prefixed name is the
          third column, so the note starts there and lines up with it without any inset. */}
      {note ? <span className={cn('flex min-w-0 items-baseline gap-2 text-meta text-muted-foreground', prefixed ? 'col-start-3 col-span-2' : 'col-start-2 col-span-2')}>{note}</span> : null}
    </>
  );
}

/** One row of a rail section, on the rail grid. Static, a button (`onClick`, optionally with a separate
 *  `action` on the value edge), or a menu trigger (`actions`). */
export function RailRow(props: RailRowProps) {
  const { onClick, action, actions, expanded, ariaLabel, testId, label, title, mono, muted } = props;
  const grid = props.labelLead === undefined ? GRID : GRID_WITH_LEAD;
  if (actions) {
    return (
      // The row carries the type step; the trigger inherits it.
      <li data-testid={testId} className={cn('-mx-2 flex min-w-0', ROW_TEXT)}>
        <ActionMenu
          items={actions}
          label={ariaLabel}
          align="left"
          openOnHover={false}
          className="min-w-0 flex-1"
          triggerClassName={cn(grid, ROW, ROW_HIT, 'mx-0 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent')}
          trigger={<RowCells {...props} />}
        />
      </li>
    );
  }
  if (onClick) {
    // The label is the real button and its ::after stretches over the whole row, so the row answers the
    // pointer everywhere while a separate `action` above that layer keeps its own click.
    const button = (
      <button
        type="button"
        data-testid={testId}
        aria-label={ariaLabel}
        aria-expanded={expanded}
        title={title}
        onClick={onClick}
        className={cn(
          'min-w-0 truncate text-left after:absolute after:inset-0 after:rounded-md focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring',
          props.detail ? NAME_BESIDE_DETAIL : 'shrink',
          muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {mono ? <span className="font-mono text-meta">{label}</span> : label}
      </button>
    );
    return (
      <li className={cn(grid, ROW, ROW_HIT)}>
        <RowCells {...props} labelAs={button} value={<>{props.value}{action ? <span className="relative flex items-center">{action}</span> : null}</>} />
      </li>
    );
  }
  return (
    <li data-testid={testId} className={cn(grid, ROW)}>
      <RowCells {...props} />
    </li>
  );
}

/** A labelled meter on the rail grid: the label in a fixed column, the bar across the middle and the
 *  figure on the value edge. `pressure` colours the fill by the shared usage ramp (a context window, a
 *  subscription limit); `progress` keeps the primary fill (a checklist, where full is good). */
export function RailMeter({ label, percent, value, title, tone = 'progress', testId, flush = false }: {
  label: string;
  percent: number;
  /** The figure on the value edge; the rounded percentage when omitted. */
  value?: ReactNode;
  title?: string;
  tone?: 'progress' | 'pressure';
  testId?: string;
  /** For a meter outside the rail (a Settings record), inside a `RailList flush`: no lead column, and the
   *  label and figure columns come from the list, sized to their widest entry, so a narrow screen leaves
   *  the room to the bar instead of to the rail's fixed label column. The label then reads as a record's
   *  name (`text-card-title`), the size of the Settings rows around it, not at the rail's conversation size. */
  flush?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, percent));
  return (
    <li data-testid={testId} title={title} className={cn('relative grid items-center', LINE, flush ? 'col-span-3 grid-cols-subgrid @max-[16rem]:grid-cols-[minmax(0,1fr)_auto] @max-[16rem]:gap-y-1 @max-[16rem]:py-1' : cn('-mx-2 grid-cols-[--spacing(4)_--spacing(22)_minmax(0,1fr)_auto] gap-x-2 px-2', ROW_TEXT))}>
      {flush ? null : <span />}
      <span className={cn('min-w-0 truncate text-foreground first-letter:uppercase', flush && 'text-card-title')}>{label}</span>
      <Progress
        value={pct}
        indicatorValue={tone === 'pressure' ? usageMeterValue(pct) : pct}
        indicatorClassName={tone === 'pressure' ? usageProgressClass(pct) : undefined}
        aria-label={label}
        className={flush ? '@max-[16rem]:order-last @max-[16rem]:col-span-2' : undefined}
      />
      <span className={cn('text-right text-meta tabular-nums text-muted-foreground', !flush && 'min-w-11')}>{value ?? `${Math.round(pct)}%`}</span>
    </li>
  );
}

/** A muted paragraph under a row, starting at the text column (a goal's latest evidence). */
export function RailNote({ children, title }: { children: ReactNode; title?: string }) {
  return <p title={title} className="line-clamp-2 pl-6 text-meta text-muted-foreground">{children}</p>;
}
